import { useMemo, useState } from "react";
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";

import EmptyStateCard from "../../components/EmptyStateCard";
import ResponsiveDialog from "../../components/ResponsiveDialog";
import StyledDataGrid from "../../components/Table/StyledDataGrid";
import { fetchDeleteRequests, restoreDeleteRequest } from "./services";
import {
  DeleteRequest,
  DeleteRequestStatus,
  FAILED_STATUSES,
  PENDING_STATUSES,
} from "./types";
import AppAlert from "../../components/AppAlert";

const QUERY_KEY = "delete-requests";
const POLL_INTERVAL_MS = 4000;

/** Shown by default: the trash proper, plus anything still running. */
const TRASH_STATUSES: readonly DeleteRequestStatus[] = [
  ...PENDING_STATUSES,
  DeleteRequestStatus.Stale,
];

/**
 * Shown only behind the history toggle: settled requests and failed ones.
 *
 * Failures are past events, not things sitting in the trash, so they belong with
 * the history. Note this also hides `RESTORE_ERROR`, which is the one failed
 * state that stays retryable — its retry button lives behind the toggle too.
 */
const HISTORY_STATUSES: readonly DeleteRequestStatus[] = [
  ...FAILED_STATUSES,
  DeleteRequestStatus.Restored,
  DeleteRequestStatus.Completed,
];

const STATUS_COLOR: Record<
  DeleteRequestStatus,
  "default" | "info" | "warning" | "success" | "error"
> = {
  [DeleteRequestStatus.Initiated]: "info",
  [DeleteRequestStatus.InProgress]: "info",
  [DeleteRequestStatus.Stale]: "warning",
  [DeleteRequestStatus.Restoring]: "info",
  [DeleteRequestStatus.Restored]: "success",
  [DeleteRequestStatus.Completed]: "default",
  [DeleteRequestStatus.Error]: "error",
  [DeleteRequestStatus.RestoreError]: "error",
};

const statusLabelId = (status: DeleteRequestStatus) =>
  `TRASH_STATUS_${status.toUpperCase()}`;

/** Days left below which the countdown turns red, then orange. */
const CRITICAL_DAYS = 3;
const WARNING_DAYS = 7;

/**
 * Colour for the days-remaining countdown.
 *
 * Semantic tokens rather than raw values: this is a deadline, and `error` /
 * `warning` are what the theme means by it in both light and dark mode.
 * Thresholds assume a retention window comfortably longer than a week — shorten
 * it and the whole column goes orange, which is arguably still the truth.
 */
const daysRemainingColor = (days: number): string => {
  if (days <= CRITICAL_DAYS) {
    return "error.main";
  }
  if (days <= WARNING_DAYS) {
    return "warning.main";
  }
  return "text.primary";
};

const TrashContent = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [showTerminal, setShowTerminal] = useState(false);
  const [pendingRestore, setPendingRestore] = useState<DeleteRequest | null>(
    null
  );

  const statuses = useMemo(
    () =>
      showTerminal ? [...TRASH_STATUSES, ...HISTORY_STATUSES] : TRASH_STATUSES,
    [showTerminal]
  );

  const {
    data: requests = [],
    isLoading,
    isError,
    error,
  } = useQuery([QUERY_KEY, statuses], () => fetchDeleteRequests(statuses), {
    // Stop once every row has settled: a trash page left open must not poll
    // for the rest of the day.
    refetchInterval: (data) =>
      (data ?? []).some((request) => PENDING_STATUSES.includes(request.status))
        ? POLL_INTERVAL_MS
        : false,
  });

  const restore = useMutation(
    (ids: number[]) =>
      // Sequential rather than parallel: the requests of one epoch share a
      // property, and the concurrency guard rejects overlapping runs.
      ids.reduce<Promise<unknown>>(
        (chain, id) => chain.then(() => restoreDeleteRequest(id)),
        Promise.resolve()
      ),
    {
      onSuccess: () => {
        enqueueSnackbar(intl.formatMessage({ id: "TRASH_RESTORE_STARTED" }), {
          variant: "success",
        });
      },
      onError: (error: Error) => {
        enqueueSnackbar(
          `${intl.formatMessage({ id: "TRASH_RESTORE_FAILED" })} ${
            error.message
          }`,
          { variant: "error" }
        );
      },
      onSettled: () => {
        setPendingRestore(null);
        // Invalidated here rather than in onSuccess: the chain is sequential, so
        // a group restore that fails on its third request has already moved the
        // first two to RESTORING server-side. Refetching only on success would
        // leave those rows showing a settled status, which also stops the grid
        // from polling for the outcome.
        queryClient.invalidateQueries(QUERY_KEY);
      },
    }
  );

  /** Requests created by the same user action, identified by a shared epoch. */
  const siblingsOf = (request: DeleteRequest) =>
    requests.filter(
      (other) => other.epoch === request.epoch && other.canRestore
    );

  const retentionDays = requests[0]?.retentionDays;

  const columns: GridColDef<DeleteRequest>[] = [
    {
      field: "target",
      headerName: intl.formatMessage({ id: "TRASH_COLUMN_TARGET" }),
      flex: 2,
      valueGetter: (_value, row) =>
        row.targetUhCatName ?? row.targetPropertyName,
      renderCell: ({ row }: GridRenderCellParams<DeleteRequest>) => (
        // Explicit column flex: `caption` renders a span, so relying on block
        // flow to stack the two lines is incidental rather than intended.
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: "100%",
          }}
        >
          <Typography variant="body2" noWrap>
            {row.targetUhCatName ?? row.targetPropertyName}
          </Typography>
          {row.targetUhCatName !== null && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary" }}
              noWrap
            >
              {row.targetPropertyName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "targetsProperty",
      headerName: intl.formatMessage({ id: "TRASH_COLUMN_TYPE" }),
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams<DeleteRequest>) => (
        <Typography variant="body2">
          <FormattedMessage
            id={
              row.targetsProperty ? "TRASH_TYPE_PROPERTY" : "TRASH_TYPE_UH_CAT"
            }
          />
        </Typography>
      ),
    },
    {
      field: "requesterName",
      headerName: intl.formatMessage({ id: "TRASH_COLUMN_REQUESTER" }),
      flex: 1.5,
      // Rendered rather than left to the default: centring the cells turns them
      // into flex containers, and a bare text node becomes an anonymous flex
      // item that no rule can reach — so it would be clipped with no ellipsis.
      // `noWrap` puts the truncation back where it belongs.
      renderCell: ({ row }: GridRenderCellParams<DeleteRequest>) => (
        <Typography variant="body2" noWrap>
          {row.requesterName}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: intl.formatMessage({ id: "TRASH_COLUMN_DELETED_AT" }),
      flex: 1.5,
      renderCell: ({ row }: GridRenderCellParams<DeleteRequest>) => (
        <Typography variant="body2">
          {intl.formatDate(row.createdAt, {
            dateStyle: "medium",
            timeStyle: "short",
          })}
        </Typography>
      ),
    },
    {
      field: "daysRemaining",
      headerName: intl.formatMessage({ id: "TRASH_COLUMN_DAYS_REMAINING" }),
      flex: 1.2,
      renderCell: ({ row }: GridRenderCellParams<DeleteRequest>) => {
        if (row.daysRemaining === null) {
          return (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              —
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{ color: daysRemainingColor(row.daysRemaining) }}
          >
            <FormattedMessage
              id="TRASH_DAYS_LEFT"
              values={{ days: row.daysRemaining }}
            />
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: intl.formatMessage({ id: "TRASH_COLUMN_STATUS" }),
      flex: 1.3,
      renderCell: ({ row }: GridRenderCellParams<DeleteRequest>) => {
        const chip = (
          <Chip
            size="small"
            color={STATUS_COLOR[row.status]}
            label={intl.formatMessage({ id: statusLabelId(row.status) })}
            sx={{ borderRadius: "5px" }}
          />
        );
        // A failed run needs a developer, so the stored message is what the
        // user needs to relay — not a generic "something went wrong".
        return row.error === null ? (
          chip
        ) : (
          <Tooltip title={row.error}>
            <span>{chip}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 60,
      align: "center",
      renderCell: ({ row }: GridRenderCellParams<DeleteRequest>) => {
        if (!row.canRestore) {
          return null;
        }
        return (
          <Tooltip title={intl.formatMessage({ id: "TRASH_RESTORE" })}>
            <IconButton
              color="primary"
              size="small"
              onClick={() => setPendingRestore(row)}
            >
              <RestoreIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const siblings = pendingRestore === null ? [] : siblingsOf(pendingRestore);

  return (
    // width: 100% is load-bearing — the page sits in a `Row` with `flex: 1`, so
    // a plain Box shrinks to its content instead of filling the view. And a
    // CUSTOM page gets no heading from the router, unlike a TABLE one, so the
    // title belongs here.
    <Box sx={{ width: "100%", mb: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        <FormattedMessage id="TRASH" />
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {retentionDays === undefined ? null : (
            <FormattedMessage
              id="TRASH_RETENTION_NOTICE"
              values={{ days: retentionDays }}
            />
          )}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showTerminal}
              onChange={(event) => setShowTerminal(event.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              <FormattedMessage id="TRASH_SHOW_TERMINAL" />
            </Typography>
          }
        />
      </Box>

      {/* Checked before the empty state: `data` defaults to [], so a failed
          fetch would otherwise claim the trash is empty when it may be full —
          the one wrong answer to give about deleted data. */}
      {isError ? (
        <AppAlert severity="error">
          {`${intl.formatMessage({ id: "TRASH_LOAD_FAILED" })} ${
            (error as Error | undefined)?.message ?? ""
          }`.trim()}
        </AppAlert>
      ) : !isLoading && requests.length === 0 ? (
        <EmptyStateCard
          logo={<DeleteOutlineIcon color="primary" />}
          text={intl.formatMessage({ id: "TRASH_EMPTY_TITLE" })}
          subText={intl.formatMessage({ id: "TRASH_EMPTY_SUBTITLE" })}
        />
      ) : (
        <StyledDataGrid
          rows={requests}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          // The shared ROW_HEIGHT is 24px, which crushes a status chip and the
          // two-line target cell. Overridden per grid — the constant is used by
          // every other table in the app. StyledDataGrid spreads its props last,
          // so this wins over its default.
          rowHeight={40}
          // autoHeight keeps the grid at its content size rather than leaving
          // whitespace under a short trash list, but the shared Paper caps at
          // 800px. 10 rows of 40px plus header and footer stay well under it;
          // picking 50 or 100 in the footer selector will spill past the Paper's
          // background.
          autoHeight
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          // A column's `align` only handles the horizontal axis, so vertical
          // centring has to come from the cell itself. Without it the two-line
          // target cell sits higher than the single-line ones and the row reads
          // as misaligned.
          sx={{
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            // Flex items do not shrink below their content, so without this the
            // `noWrap` truncation in the cells never gets a chance to kick in.
            "& .MuiDataGrid-cell > *": {
              minWidth: 0,
            },
          }}
        />
      )}

      <ResponsiveDialog
        open={pendingRestore !== null}
        onClose={() => setPendingRestore(null)}
        dialogName={false}
        fullWidth
        title={intl.formatMessage({ id: "TRASH_RESTORE_CONFIRM_TITLE" })}
        onSubmit={() => restore.mutate(siblings.map((s) => s.id))}
        loading={restore.isLoading}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          <FormattedMessage
            id="TRASH_RESTORE_CONFIRM_BODY"
            values={{
              target:
                pendingRestore?.targetUhCatName ??
                pendingRestore?.targetPropertyName ??
                "",
            }}
          />
        </Typography>
        {siblings.length > 1 && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <FormattedMessage
              id="TRASH_RESTORE_GROUP_WARNING"
              values={{ count: siblings.length }}
            />
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          <FormattedMessage id="TRASH_RESTORE_RECOMPUTE_WARNING" />
        </Typography>
      </ResponsiveDialog>
    </Box>
  );
};

export default TrashContent;
